import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Search, Send, MessageSquare, ArrowLeft, LoaderCircle } from "lucide-react";
import api from "../api/axios";
import { UserContext } from "../context/UserContext";
import { AuthContext } from "../context/AuthContext";
import toast from "react-hot-toast";
import getInitials from "../utils/getInitials";

const SUPPORT_CACHE_KEY = "mekpay_support_users_v1";

const formatTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export default function ChatPage() {
  const { user: me } = useContext(UserContext);
  const { user: authUser } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [supportUsers, setSupportUsers] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const searchTimer = useRef(null);

  const effectiveRole = authUser?.role || me?.role;

  const saveSupportCache = (list) => {
    try {
      localStorage.setItem(SUPPORT_CACHE_KEY, JSON.stringify(list));
    } catch {
      // ignore storage failures
    }
  };

  const loadSupportCache = () => {
    try {
      const raw = localStorage.getItem(SUPPORT_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  // Load conversations
  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConversations(data);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (effectiveRole !== "user") return;

    const cached = loadSupportCache();
    if (cached.length > 0) {
      setSupportUsers(cached);
    }

    const fetchSupportUsers = async () => {
      setSupportLoading(true);
      setSupportError("");
      try {
        const { data } = await api.get("/chat/support-users");
        const normalized = (Array.isArray(data) ? data : []).map((u) => ({
          ...u,
          isOnline: false,
        }));
        setSupportUsers(normalized);
        if (normalized.length > 0) {
          saveSupportCache(normalized);
        }
      } catch (error) {
        const fallback = loadSupportCache();
        setSupportUsers(fallback);
        setSupportError(
          fallback.length > 0
            ? "Support is available. Replies within 15 mins."
            : (error.response?.data?.message || "Unable to load support contacts")
        );
      }
      setSupportLoading(false);
    };
    fetchSupportUsers();
  }, [effectiveRole]);

  // Load messages for active conversation + poll every 3s
  const fetchMessages = useCallback(async (userId) => {
    try {
      const { data } = await api.get(`/chat/${userId}`);
      setMessages(data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load messages");
    }
  }, []);

  useEffect(() => {
    if (!activeConvo) {
      clearInterval(pollRef.current);
      return;
    }
    setLoadingMsgs(true);
    fetchMessages(activeConvo._id).finally(() => setLoadingMsgs(false));
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(activeConvo._id), 3000);
    return () => clearInterval(pollRef.current);
  }, [activeConvo, fetchMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Debounced user search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/chat/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery]);

  const openConvo = (user) => {
    setActiveConvo(user);
    setSearchQuery("");
    setSearchResults([]);
    setMessages([]);
    // Optimistically update unread
    setConversations((prev) =>
      prev.map((c) => (c.user._id === user._id ? { ...c, unread: 0 } : c))
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeConvo || sending) return;
    const content = input.trim();
    setInput("");
    setSending(true);

    // Optimistic message
    const optimistic = {
      _id: `temp-${Date.now()}`,
      sender: { _id: me?.id || me?._id, fullName: me?.name },
      content,
      createdAt: new Date().toISOString(),
      read: false,
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const { data } = await api.post(`/chat/${activeConvo._id}`, { content });
      setMessages((prev) => prev.map((m) => (m._id === optimistic._id ? data : m)));
      fetchConversations();
    } catch (error) {
      setMessages((prev) => prev.filter((m) => m._id !== optimistic._id));
      setInput(content);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const isMine = (msg) => {
    const senderId = msg.sender?._id || msg.sender;
    return senderId?.toString() === (me?.id || me?._id || "").toString();
  };

  return (
    <div className="mx-auto max-w-6xl text-white h-[calc(100vh-8rem)]">
      <div className="flex h-full rounded-[2rem] border border-white/10 bg-slate-900/75 backdrop-blur-xl overflow-hidden">
        {/* ── LEFT: Conversation list ── */}
        <div
          className={`flex flex-col border-r border-white/10 bg-slate-900/60 transition-all ${
            activeConvo ? "hidden md:flex md:w-72 lg:w-80 flex-shrink-0" : "flex w-full md:w-72 lg:w-80 flex-shrink-0"
          }`}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-bold mb-3">Messages</h2>
            <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2">
              <Search size={16} className="text-slate-400 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent text-sm text-white placeholder:text-slate-400 outline-none"
              />
            </div>
          </div>

          {/* Search results */}
          {effectiveRole === "user" && (
            <div className="border-b border-white/10 bg-slate-800/40 px-4 py-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Support</p>
              {supportLoading && <p className="text-xs text-slate-400">Loading support contacts...</p>}
              {!supportLoading && supportError && <p className="text-xs text-rose-300">{supportError}</p>}
              {!supportLoading && !supportError && supportUsers.length === 0 && (
                <p className="text-xs text-slate-400">No support agent available right now.</p>
              )}
              <div className="space-y-2">
                {supportUsers.map((supportUser) => (
                  <button
                    key={supportUser._id}
                    onClick={() => openConvo(supportUser)}
                    className="flex w-full items-center gap-3 rounded-xl border border-cyan-300/20 bg-cyan-500/10 px-3 py-2 text-left transition hover:bg-cyan-500/20"
                  >
                    <span aria-hidden="true" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cyan-300/15 text-xs font-semibold text-cyan-200">
                      {getInitials(supportUser.fullName)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-white">{supportUser.fullName}</p>
                      <p className="truncate text-xs text-cyan-100/80">
                        Admin Support • Online, replies within 15 mins
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {(searchResults.length > 0 || searching) && (
            <div className="border-b border-white/10 bg-slate-800/60">
              {searching && <p className="px-4 py-2 text-xs text-slate-400">Searching…</p>}
              {searchResults.map((u) => (
                <button
                  key={u._id}
                  onClick={() => openConvo(u)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition text-left"
                >
                  <span aria-hidden="true" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cyan-300/15 text-xs font-semibold text-cyan-200">
                    {getInitials(u.fullName)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.fullName}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400">
                <MessageSquare size={32} />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs text-slate-500">Search for a user to start chatting</p>
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.user._id}
                onClick={() => openConvo(c.user)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition text-left border-b border-white/5 ${
                  activeConvo?._id === c.user._id ? "bg-cyan-300/10" : "hover:bg-white/5"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-300/15 text-xs font-semibold text-cyan-200">
                    {getInitials(c.user.fullName)}
                  </span>
                  {c.unread > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-cyan-400 text-slate-900 text-[10px] font-bold flex items-center justify-center">
                      {c.unread}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate">{c.user.fullName}</span>
                    <span className="text-[10px] text-slate-500 flex-shrink-0 ml-2">
                      {formatTime(c.lastMessage.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{c.lastMessage.content}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Message view ── */}
        {activeConvo ? (
          <div className="flex flex-col flex-1 min-w-0">
            {/* Chat header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-slate-900/60 flex-shrink-0">
              <button
                onClick={() => setActiveConvo(null)}
                className="md:hidden p-1 hover:bg-white/10 rounded-lg text-slate-300"
              >
                <ArrowLeft size={20} />
              </button>
              <span aria-hidden="true" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cyan-300/15 text-xs font-semibold text-cyan-200">
                {getInitials(activeConvo.fullName)}
              </span>
              <div>
                <p className="font-semibold text-white">{activeConvo.fullName}</p>
                <p className="text-xs text-slate-400">{activeConvo.email}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,rgba(15,23,42,0.98),rgba(15,23,42,0.9)),radial-gradient(circle_at_top,rgba(6,182,212,0.08),transparent_35%)] p-4 space-y-3">
              {loadingMsgs && (
                <p className="text-center text-xs text-slate-500">Loading…</p>
              )}
              {messages.map((msg) => {
                const mine = isMine(msg);
                return (
                  <div key={msg._id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                    {!mine && (
                      <span aria-hidden="true" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cyan-300/15 text-[10px] font-semibold text-cyan-200">
                        {getInitials(activeConvo.fullName)}
                      </span>
                    )}
                    <div
                      className={`max-w-[76%] rounded-[1.35rem] px-4 py-2.5 text-sm shadow-lg ${
                        mine
                          ? "bg-cyan-400 text-slate-950 rounded-br-md"
                          : "border border-white/10 bg-slate-800 text-white rounded-bl-md"
                      }`}
                    >
                      <p className="break-words leading-relaxed">{msg.content}</p>
                      <p className={`mt-1 text-[10px] ${mine ? "text-slate-700" : "text-slate-400"} text-right`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="flex items-center gap-3 p-4 border-t border-white/10 flex-shrink-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-cyan-400/50"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                aria-label={sending ? "Sending message" : "Send message"}
                aria-busy={sending}
                className="flex-shrink-0 rounded-xl bg-cyan-400 p-2.5 text-slate-900 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {sending ? <LoaderCircle size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </form>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 flex-col items-center justify-center gap-3 text-slate-400">
            <MessageSquare size={48} />
            <p className="text-lg font-semibold text-white">Your Messages</p>
            <p className="text-sm">Select a conversation or search for a user to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}
